import { useState, useCallback, useEffect } from 'react';

export interface SchemaProperty {
  name: string;
  type: string;
  required: boolean;
  xIdentity: boolean;
  level: number;
  parent?: number;
  collapsed?: boolean;
  value?: string;
  validationRule?: {
    type: string;
    value: string;
    elementType?: string;
    values?: string;
    min?: string;
    max?: string;
  };
}

export interface UseRequestSchemaReturn {
  // State
  schemaProperties: SchemaProperty[];
  setSchemaProperties: React.Dispatch<React.SetStateAction<SchemaProperty[]>>;
  schemaJson: string;
  setSchemaJson: React.Dispatch<React.SetStateAction<string>>;
  schemaJsonValid: boolean;
  schemaValidationErrors: string[];
  showRequestJson: boolean;
  setShowRequestJson: React.Dispatch<React.SetStateAction<boolean>>;
  metadataSchemaProperties: SchemaProperty[];
  setMetadataSchemaProperties: React.Dispatch<React.SetStateAction<SchemaProperty[]>>;
  metadataSchemaJson: string;
  setMetadataSchemaJson: React.Dispatch<React.SetStateAction<string>>;
  metadataSchemaJsonValid: boolean;
  metadataSchemaValidationErrors: string[];
  showMetadataJson: boolean;
  setShowMetadataJson: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Schema property functions
  togglePropertyCollapsed: (index: number) => void;
  isChildVisible: (childProperty: SchemaProperty, parentIndex?: number, properties?: SchemaProperty[]) => boolean;
  addSchemaProperty: () => void;
  addChildProperty: (parentIndex: number) => void;
  removeProperty: (index: number) => void;
  updatePropertyName: (index: number, name: string) => void;
  updatePropertyType: (index: number, type: string) => void;
  updatePropertyRequired: (index: number, required: boolean) => void;
  updatePropertyXIdentity: (index: number, xIdentity: boolean) => void;
  updateValidationRuleType: (index: number, ruleType: string) => void;
  updateValidationRuleValue: (index: number, value: string) => void;
  updateValidationRuleElementType: (index: number, elementType: string) => void;
  updateValidationRuleValues: (index: number, values: string) => void;
  updateValidationRuleMin: (index: number, min: string) => void;
  updateValidationRuleMax: (index: number, max: string) => void;
  updateSchemaJsonFromProperties: (properties?: SchemaProperty[]) => void;
  updateSchemaJson: (properties?: SchemaProperty[]) => void;
  parseSchemaPreservingCollapsedState: (schemaText: string) => SchemaProperty[];
  expandAllProperties: () => void;
  collapseAllProperties: () => void;
  syncVisualToJson: () => void;
  syncJsonToVisual: () => void;
  syncRequestVisualWithJson: () => void;
  
  // Metadata functions
  addMetadataProperty: () => void;
  addMetadataChildProperty: (parentIndex: number) => void;
  removeMetadataProperty: (index: number) => void;
  updateMetadataPropertyName: (index: number, name: string) => void;
  updateMetadataPropertyType: (index: number, type: string) => void;
  updateMetadataPropertyValue: (index: number, value: string) => void;
  toggleMetadataPropertyCollapsed: (index: number) => void;
  isMetadataChildVisible: (property: SchemaProperty, parentIndex?: number) => boolean;
  updateMetadataJsonFromProperties: (properties?: SchemaProperty[]) => void;
  syncMetadataVisualToJson: () => void;
  syncMetadataJsonToVisual: () => void;
  buildMetadataJsonFromProperties: (properties: SchemaProperty[]) => any;
  
  // Shared utilities
  parseValueType: (value: string) => { type: string; parsedValue: any };
  parseSchemaToProperties: (schemaText: string) => SchemaProperty[];
  buildSchemaFromProperties: (properties?: SchemaProperty[]) => any;
}

export const useRequestSchema = (
  setIsModified: (modified: boolean) => void,
  parseSchemaToPropertiesFn: (schemaText: string) => SchemaProperty[]
): UseRequestSchemaReturn => {
  // Schema state
  const [schemaProperties, setSchemaProperties] = useState<SchemaProperty[]>([]);
  const [schemaJson, setSchemaJson] = useState<string>('');
  const [schemaJsonValid, setSchemaJsonValid] = useState(true);
  const [schemaValidationErrors, setSchemaValidationErrors] = useState<string[]>([]);
  const [showRequestJson, setShowRequestJson] = useState(true);
  
  // Metadata state
  const [metadataSchemaProperties, setMetadataSchemaProperties] = useState<SchemaProperty[]>([]);
  const [metadataSchemaJson, setMetadataSchemaJson] = useState<string>('{}');
  const [metadataSchemaJsonValid, setMetadataSchemaJsonValid] = useState(true);
  const [metadataSchemaValidationErrors, setMetadataSchemaValidationErrors] = useState<string[]>([]);
  const [showMetadataJson, setShowMetadataJson] = useState(true);

  // Validate schema JSON whenever it changes
  useEffect(() => {
    // Skip validation for empty schemas
    if (!schemaJson.trim()) {
      setSchemaJsonValid(true);
      setSchemaValidationErrors([]);
      return;
    }

    try {
      const parsed = JSON.parse(schemaJson);
      
      // Validate it's an object
      if (typeof parsed !== 'object' || parsed === null) {
        setSchemaJsonValid(false);
        setSchemaValidationErrors(['Request schema must be a JSON object']);
        return;
      }
      
      // Validate basic JSON Schema structure
      if (parsed.type && typeof parsed.type !== 'string') {
        setSchemaJsonValid(false);
        setSchemaValidationErrors(['Schema "type" field must be a string']);
        return;
      }
      
      // If it has properties, validate they're in an object
      if (parsed.properties && typeof parsed.properties !== 'object') {
        setSchemaJsonValid(false);
        setSchemaValidationErrors(['Schema "properties" field must be an object']);
        return;
      }
      
      // If it has required, validate it's an array
      if (parsed.required && !Array.isArray(parsed.required)) {
        setSchemaJsonValid(false);
        setSchemaValidationErrors(['Schema "required" field must be an array']);
        return;
      }
      
      // All validations passed
      setSchemaJsonValid(true);
      setSchemaValidationErrors([]);
    } catch (error: any) {
      setSchemaJsonValid(false);
      setSchemaValidationErrors([`Invalid JSON: ${error.message}`]);
    }
  }, [schemaJson]);

  // Schema property functions
  const togglePropertyCollapsed = useCallback((index: number) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].collapsed = !newProperties[index].collapsed;
      return newProperties;
    });
  }, []);

  const isChildVisible = useCallback((childProperty: SchemaProperty, parentIndex?: number, properties?: SchemaProperty[]): boolean => {
    if (parentIndex === undefined) return true;
    
    const propertiesArray = properties || schemaProperties;
    const parent = propertiesArray[parentIndex];
    if (!parent) return false;
    
    if (parent.collapsed) return false;
    
    if (parent.parent !== undefined) {
      return isChildVisible(parent, parent.parent, propertiesArray);
    }
    
    return true;
  }, [schemaProperties]);

  const addSchemaProperty = useCallback(() => {
    const newProperty: SchemaProperty = {
      name: '',
      type: 'string',
      required: false,
      xIdentity: false,
      level: 0,
      collapsed: true
    };
    setSchemaProperties(prev => [...prev, newProperty]);
    setIsModified(true);
  }, [setIsModified]);

  const addChildProperty = useCallback((parentIndex: number) => {
    setSchemaProperties(prev => {
      const buildPath = (index: number): string[] => {
        const path: string[] = [];
        let current: number | undefined = index;
        
        while (current !== undefined) {
          const prop: SchemaProperty = prev[current];
          if (prop.name) {
            path.unshift(prop.name);
          }
          current = prop.parent;
        }
        
        return path;
      };
      
      const parentPath = buildPath(parentIndex);
      
      try {
        const currentSchema = JSON.parse(schemaJson);
        
        let targetObject = currentSchema;
        for (const pathSegment of parentPath) {
          if (!targetObject.properties) {
            targetObject.properties = {};
          }
          if (!targetObject.properties[pathSegment]) {
            targetObject.properties[pathSegment] = { type: 'object', properties: {} };
          }
          targetObject = targetObject.properties[pathSegment];
        }
        
        if (!targetObject.properties) {
          targetObject.properties = {};
        }
        
        let newPropName = 'new_attribute';
        let counter = 1;
        while (targetObject.properties[newPropName]) {
          newPropName = `new_attribute_${counter}`;
          counter++;
        }
        
        targetObject.properties[newPropName] = {
          type: 'string'
        };
        
        setIsModified(true);
        
        const newJsonString = JSON.stringify(currentSchema, null, 2);
        setSchemaJson(newJsonString);
        
        setTimeout(() => {
          const newProperties = parseSchemaToPropertiesFn(newJsonString);
          const parentName = prev[parentIndex].name;
          const parentLevel = prev[parentIndex].level;
          
          const updatedProperties = newProperties.map(prop => {
            if (prop.name === parentName && prop.level === parentLevel) {
              return { ...prop, collapsed: false };
            }
            return prop;
          });
          
          setSchemaProperties(updatedProperties);
        }, 0);
        
        return prev;
      } catch (error) {
        console.error('Error adding child property:', error);
        return prev;
      }
    });
  }, [schemaJson, setIsModified, parseSchemaToPropertiesFn]);

  const removeProperty = useCallback((index: number) => {
    setSchemaProperties(prev => {
      const toRemove = new Set([index]);
      
      const findDescendants = (parentIdx: number) => {
        prev.forEach((prop, idx) => {
          if (prop.parent === parentIdx) {
            toRemove.add(idx);
            findDescendants(idx);
          }
        });
      };
      
      findDescendants(index);
      
      const sortedIndices = Array.from(toRemove).sort((a, b) => b - a);
      let newProperties = [...prev];
      
      sortedIndices.forEach(idx => {
        newProperties.splice(idx, 1);
      });
      
      const indexMapping = new Map();
      let newIndex = 0;
      prev.forEach((_, oldIndex) => {
        if (!toRemove.has(oldIndex)) {
          indexMapping.set(oldIndex, newIndex);
          newIndex++;
        }
      });
      
      newProperties = newProperties.map(prop => {
        if (prop.parent !== undefined && indexMapping.has(prop.parent)) {
          return { ...prop, parent: indexMapping.get(prop.parent) };
        } else if (prop.parent !== undefined) {
          return { ...prop, parent: undefined, level: 0 };
        }
        return prop;
      });
      
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updatePropertyName = useCallback((index: number, name: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].name = name;
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updatePropertyType = useCallback((index: number, type: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].type = type;
      
      if (!['string', 'number', 'boolean'].includes(type)) {
        newProperties[index].required = false;
        newProperties[index].xIdentity = false;
      }
      
      if (type !== 'number' && newProperties[index].validationRule?.type === 'range') {
        delete newProperties[index].validationRule;
      }
      
      if (type === 'number' && newProperties[index].validationRule?.type === 'regex') {
        delete newProperties[index].validationRule;
      }
      
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updatePropertyRequired = useCallback((index: number, required: boolean) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].required = required;
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updatePropertyXIdentity = useCallback((index: number, xIdentity: boolean) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].xIdentity = xIdentity;
      if (xIdentity) {
        newProperties[index].required = true;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateValidationRuleType = useCallback((index: number, ruleType: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      if (ruleType === '') {
        delete newProperties[index].validationRule;
      } else {
        newProperties[index].validationRule = { type: ruleType, value: '' };
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateValidationRuleValue = useCallback((index: number, value: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.value = value;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateValidationRuleElementType = useCallback((index: number, elementType: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.elementType = elementType;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateValidationRuleValues = useCallback((index: number, values: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.values = values;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateValidationRuleMin = useCallback((index: number, min: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.min = min;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateValidationRuleMax = useCallback((index: number, max: string) => {
    setSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.max = max;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const buildSchemaFromProperties = useCallback((properties?: SchemaProperty[]): any => {
    const propsToUse = properties || schemaProperties;
    if (propsToUse.length === 0) {
      return {};
    }

    const buildObjectProperties = (properties: SchemaProperty[], level: number, parentIndex?: number): any => {
      const obj: any = {
        type: 'object',
        properties: {},
        required: []
      };

      properties.forEach((prop, currentIndex) => {
        if (prop.level === level && (parentIndex === undefined || prop.parent === parentIndex)) {
          if (!prop.name.trim()) return;

          const propertySchema: any = {};

          switch (prop.type) {
            case 'string':
              propertySchema.type = 'string';
              break;
            case 'number':
              propertySchema.type = 'number';
              break;
            case 'boolean':
              propertySchema.type = 'boolean';
              break;
            case 'array':
              propertySchema.type = 'array';
              propertySchema.items = { type: 'string' };
              break;
            case 'object':
              const hasChildren = properties.some(p => p.parent === currentIndex);
              if (hasChildren) {
                const childSchema = buildObjectProperties(properties, level + 1, currentIndex);
                propertySchema.type = 'object';
                propertySchema.properties = childSchema.properties;
                if (childSchema.required.length > 0) {
                  propertySchema.required = childSchema.required;
                }
              } else {
                propertySchema.type = 'object';
                propertySchema.properties = {};
              }
              break;
          }

          if (prop.validationRule) {
            switch (prop.validationRule.type) {
              case 'equals':
                if (prop.type === 'number') {
                  const numericValue = parseFloat(prop.validationRule.value);
                  propertySchema.const = isNaN(numericValue) ? prop.validationRule.value : numericValue;
                } else {
                  propertySchema.const = prop.validationRule.value;
                }
                break;
              case 'contains':
                const escapedContainsValue = prop.validationRule.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                propertySchema.pattern = `.*${escapedContainsValue}.*`;
                break;
              case 'regex':
                propertySchema.pattern = prop.validationRule.value;
                break;
              case 'range':
                if (prop.type === 'number' && prop.validationRule.value.includes('-')) {
                  const parts = prop.validationRule.value.split('-');
                  const minStr = parts[0].trim();
                  const maxStr = parts[1] ? parts[1].trim() : '';
                  
                  if (minStr) {
                    const min = parseFloat(minStr);
                    if (!isNaN(min)) propertySchema.minimum = min;
                  }
                  if (maxStr) {
                    const max = parseFloat(maxStr);
                    if (!isNaN(max)) propertySchema.maximum = max;
                  }
                }
                break;
              case 'array-all':
                propertySchema['x-array-validation'] = {
                  type: 'all',
                  elementType: prop.validationRule.elementType || 'string',
                  values: prop.validationRule.values ? prop.validationRule.values.split(',').map(v => v.trim()).filter(v => v) : undefined
                };
                break;
              case 'array-any':
                propertySchema['x-array-validation'] = {
                  type: 'any',
                  elementType: prop.validationRule.elementType || 'string',
                  values: prop.validationRule.values ? prop.validationRule.values.split(',').map(v => v.trim()).filter(v => v) : undefined
                };
                break;
              case 'array-length':
                propertySchema['x-array-validation'] = {
                  type: 'length',
                  min: prop.validationRule.min ? parseInt(prop.validationRule.min) : undefined,
                  max: prop.validationRule.max ? parseInt(prop.validationRule.max) : undefined
                };
                break;
            }
          }

          if (prop.xIdentity) {
            propertySchema['x-identity'] = true;
          }

          obj.properties[prop.name] = propertySchema;

          if (prop.required) {
            obj.required.push(prop.name);
          }
        }
      });

      return obj;
    };

    const rootSchema = buildObjectProperties(propsToUse, 0);
    return rootSchema;
  }, [schemaProperties]);

  const updateSchemaJson = useCallback((properties?: SchemaProperty[]) => {
    const propsToUse = properties || schemaProperties;
    if (propsToUse.length === 0) {
      setSchemaJson('');
      return;
    }

    const schema = buildSchemaFromProperties(propsToUse);
    const jsonString = JSON.stringify(schema, null, 2);
    setSchemaJson(jsonString);
    setIsModified(true);
  }, [schemaProperties, buildSchemaFromProperties, setIsModified]);

  const updateSchemaJsonFromProperties = useCallback((properties?: SchemaProperty[]) => {
    updateSchemaJson(properties);
  }, [updateSchemaJson]);

  const parseSchemaPreservingCollapsedState = useCallback((schemaText: string): SchemaProperty[] => {
    const newProperties = parseSchemaToPropertiesFn(schemaText);
    
    if (schemaProperties.length > 0) {
      const existingCollapsedState = new Map<string, boolean>();
      
      const buildCollapsedMap = (props: SchemaProperty[], path = '') => {
        props.forEach(prop => {
          const fullPath = path ? `${path}.${prop.name}` : prop.name;
          if (prop.collapsed !== undefined) {
            existingCollapsedState.set(fullPath, prop.collapsed);
          }
        });
      };
      
      buildCollapsedMap(schemaProperties);
      
      const applyCollapsedState = (props: SchemaProperty[], path = '') => {
        props.forEach(prop => {
          const fullPath = path ? `${path}.${prop.name}` : prop.name;
          if (existingCollapsedState.has(fullPath)) {
            prop.collapsed = existingCollapsedState.get(fullPath);
          }
        });
      };
      
      applyCollapsedState(newProperties);
    }
    
    return newProperties;
  }, [schemaProperties, parseSchemaToPropertiesFn]);

  const syncRequestVisualWithJson = useCallback(() => {
    if (schemaJson) {
      try {
        const newProperties = parseSchemaToPropertiesFn(schemaJson);
        setSchemaProperties(newProperties);
        setIsModified(true);
      } catch (e) {
        // Failed to sync visual editor with JSON
      }
    }
  }, [schemaJson, parseSchemaToPropertiesFn, setIsModified]);

  const expandAllProperties = useCallback(() => {
    setSchemaProperties(prev => prev.map(prop => ({ ...prop, collapsed: false })));
  }, []);

  const collapseAllProperties = useCallback(() => {
    setSchemaProperties(prev => prev.map(prop => ({ ...prop, collapsed: true })));
  }, []);

  const syncVisualToJson = useCallback(() => {
    updateSchemaJsonFromProperties(schemaProperties);
  }, [schemaProperties, updateSchemaJsonFromProperties]);

  const syncJsonToVisual = useCallback(() => {
    syncRequestVisualWithJson();
  }, [syncRequestVisualWithJson]);

  // Metadata functions
  const buildMetadataJsonFromProperties = useCallback((properties: SchemaProperty[]): any => {
    const result: any = {};
    
    properties.forEach((prop, index) => {
      if (prop.level === 0 && prop.name) {
        const { parsedValue } = parseValueType(prop.value || '');
        result[prop.name] = parsedValue;
      }
    });
    
    return result;
  }, []);

  const parseValueType = useCallback((value: string): { type: string; parsedValue: any } => {
    if (!value || value.trim() === '') {
      return { type: 'string', parsedValue: '' };
    }
    
    const trimmed = value.trim();
    
    if (trimmed === 'true') {
      return { type: 'boolean', parsedValue: true };
    }
    if (trimmed === 'false') {
      return { type: 'boolean', parsedValue: false };
    }
    
    if (/^-?\d+$/.test(trimmed)) {
      return { type: 'number', parsedValue: parseInt(trimmed, 10) };
    }
    
    if (/^-?\d+\.\d+$/.test(trimmed)) {
      return { type: 'number', parsedValue: parseFloat(trimmed) };
    }
    
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return { type: Array.isArray(parsed) ? 'array' : 'object', parsedValue: parsed };
      } catch {
        return { type: 'string', parsedValue: value };
      }
    }
    
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      try {
        const parsed = JSON.parse(trimmed);
        return { type: 'string', parsedValue: parsed };
      } catch {
        return { type: 'string', parsedValue: value };
      }
    }
    
    return { type: 'string', parsedValue: value };
  }, []);

  const updateMetadataJsonFromProperties = useCallback((properties?: SchemaProperty[]) => {
    const propsToUse = properties || metadataSchemaProperties;
    const jsonObj = buildMetadataJsonFromProperties(propsToUse);
    setMetadataSchemaJson(JSON.stringify(jsonObj, null, 2));
    setIsModified(true);
  }, [metadataSchemaProperties, buildMetadataJsonFromProperties, setIsModified]);

  const addMetadataProperty = useCallback(() => {
    setMetadataSchemaProperties(prev => [...prev, {
      name: '',
      type: 'string',
      required: false,
      xIdentity: false,
      level: 0,
      collapsed: false
    }]);
    setIsModified(true);
  }, [setIsModified]);

  const addMetadataChildProperty = useCallback((parentIndex: number) => {
    setMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      const parentProp = newProps[parentIndex];
      
      if (parentProp.type !== 'object') {
        parentProp.type = 'object';
      }
      
      const newProp = {
        name: '',
        type: 'string',
        required: false,
        xIdentity: false,
        level: parentProp.level + 1,
        parent: parentIndex,
        collapsed: false
      };
      
      let insertIndex = parentIndex + 1;
      while (insertIndex < newProps.length && newProps[insertIndex].level > parentProp.level) {
        insertIndex++;
      }
      
      newProps.splice(insertIndex, 0, newProp);
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const removeMetadataProperty = useCallback((index: number) => {
    setMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      const prop = newProps[index];
      
      const toRemove = [index];
      for (let i = index + 1; i < newProps.length; i++) {
        if (newProps[i].level > prop.level) {
          toRemove.push(i);
        } else {
          break;
        }
      }
      
      const filtered = newProps.filter((_, i) => !toRemove.includes(i));
      
      updateMetadataJsonFromProperties(filtered);
      return filtered;
    });
  }, [updateMetadataJsonFromProperties]);

  const toggleMetadataPropertyCollapsed = useCallback((index: number) => {
    setMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      newProps[index].collapsed = !newProps[index].collapsed;
      return newProps;
    });
  }, []);

  const isMetadataChildVisible = useCallback((property: SchemaProperty, parentIndex?: number): boolean => {
    if (parentIndex === undefined) return true;
    
    const parent = metadataSchemaProperties[parentIndex];
    if (!parent) return false;
    
    if (parent.collapsed) return false;
    
    if (parent.parent !== undefined) {
      return isMetadataChildVisible(parent, parent.parent);
    }
    
    return true;
  }, [metadataSchemaProperties]);

  const updateMetadataPropertyName = useCallback((index: number, name: string) => {
    setMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      newProps[index].name = name;
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateMetadataPropertyType = useCallback((index: number, type: string) => {
    setMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      newProps[index].type = type;
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateMetadataPropertyValue = useCallback((index: number, value: string) => {
    setMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      (newProps[index] as any).value = value;
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const syncMetadataVisualToJson = useCallback(() => {
    updateMetadataJsonFromProperties(metadataSchemaProperties);
  }, [metadataSchemaProperties, updateMetadataJsonFromProperties]);

  const syncMetadataJsonToVisual = useCallback(() => {
    if (metadataSchemaJson && metadataSchemaJson !== '{}') {
      try {
        const parsed = JSON.parse(metadataSchemaJson);
        const props: SchemaProperty[] = [];
        
        const parseObject = (obj: any, level: number, parentIndex?: number) => {
          Object.keys(obj).forEach(key => {
            const val = obj[key];
            let type = 'string';
            let valueStr = '';
            
            if (typeof val === 'number') {
              type = 'number';
              valueStr = String(val);
            } else if (typeof val === 'boolean') {
              type = 'boolean';
              valueStr = String(val);
            } else if (Array.isArray(val)) {
              type = 'array';
              valueStr = JSON.stringify(val);
            } else if (typeof val === 'object' && val !== null) {
              type = 'object';
            } else {
              type = 'string';
              valueStr = String(val);
            }
            
            const propIndex = props.length;
            props.push({
              name: key,
              type,
              required: false,
              xIdentity: false,
              level,
              parent: parentIndex,
              collapsed: false,
              value: valueStr
            });
            
            if (type === 'object') {
              parseObject(val, level + 1, propIndex);
            }
          });
        };
        
        parseObject(parsed, 0);
        setMetadataSchemaProperties(props);
        setIsModified(true);
      } catch (e) {
        console.error('Failed to sync metadata JSON to visual:', e);
      }
    }
  }, [metadataSchemaJson, setIsModified]);

  return {
    // State
    schemaProperties,
    setSchemaProperties,
    schemaJson,
    setSchemaJson,
    schemaJsonValid,
    schemaValidationErrors,
    showRequestJson,
    setShowRequestJson,
    metadataSchemaProperties,
    setMetadataSchemaProperties,
    metadataSchemaJson,
    setMetadataSchemaJson,
    metadataSchemaJsonValid,
    metadataSchemaValidationErrors,
    showMetadataJson,
    setShowMetadataJson,
    
    // Schema property functions
    togglePropertyCollapsed,
    isChildVisible,
    addSchemaProperty,
    addChildProperty,
    removeProperty,
    updatePropertyName,
    updatePropertyType,
    updatePropertyRequired,
    updatePropertyXIdentity,
    updateValidationRuleType,
    updateValidationRuleValue,
    updateValidationRuleElementType,
    updateValidationRuleValues,
    updateValidationRuleMin,
    updateValidationRuleMax,
    updateSchemaJsonFromProperties,
    updateSchemaJson,
    parseSchemaPreservingCollapsedState,
    expandAllProperties,
    collapseAllProperties,
    syncVisualToJson,
    syncJsonToVisual,
    syncRequestVisualWithJson,
    
    // Metadata functions
    addMetadataProperty,
    addMetadataChildProperty,
    removeMetadataProperty,
    updateMetadataPropertyName,
    updateMetadataPropertyType,
    updateMetadataPropertyValue,
    toggleMetadataPropertyCollapsed,
    isMetadataChildVisible,
    updateMetadataJsonFromProperties,
    syncMetadataVisualToJson,
    syncMetadataJsonToVisual,
    buildMetadataJsonFromProperties,
    
    // Shared utilities
    parseValueType,
    parseSchemaToProperties: parseSchemaToPropertiesFn,
    buildSchemaFromProperties,
  };
};
